/*
 * TTS instant search: the header search box's as-you-type dropdown plus
 * "Recent searches" (replaces the Searchanise autocomplete). Results markup
 * comes from sections/tts-search-suggest.liquid via Shopify's /search/suggest.
 */
(() => {
  if (customElements.get('tts-instant-search')) return;

  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'tts-search-suggest';
  const RECENT_KEY = 'tts:recent-searches';
  const RECENT_MAX = 5;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const escapeHtml = (value) => value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // ---- Recent searches (kept only in this shopper's browser) ----
  const recent = {
    read() {
      try {
        const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
      } catch (error) {
        return [];
      }
    },
    add(term) {
      const clean = (term || '').replace(/\*+$/, '').trim();
      if (clean.length < 2) return;
      const list = recent.read().filter((item) => item.toLowerCase() !== clean.toLowerCase());
      list.unshift(clean);
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (error) { /* storage blocked: skip */ }
    },
    clear() {
      try {
        window.localStorage.removeItem(RECENT_KEY);
      } catch (error) { /* storage blocked: skip */ }
    },
    html() {
      const list = recent.read();
      if (list.length === 0) return '';
      const pills = list.map((term) =>
        `<li><a href="${root()}search?q=${encodeURIComponent(term)}" class="tts-ss__pill" data-tts-ss-option>${escapeHtml(term)}</a></li>`
      ).join('');
      return `<div class="tts-ss tts-ss--recent"><div class="tts-ss__group tts-ss__recent">
        <div class="tts-ss__heading-row"><p class="tts-ss__heading">Recent searches</p>
        <button type="button" class="tts-ss__clear" data-tts-ss-clear-recent>Clear</button></div>
        <ul class="tts-ss__pills" aria-label="Recent searches">${pills}</ul></div></div>`;
    }
  };

  // Record searches that land on the results page (header box, phone search, "See all results").
  if (/\/search\/?$/.test(window.location.pathname)) {
    recent.add(new URLSearchParams(window.location.search).get('q'));
  }

  // ---- Desktop header search box ----
  class TtsInstantSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.panel = this.querySelector('[data-tts-ss-panel]');
      this.status = this.querySelector('[data-tts-ss-status]');
      this.emptyTemplate = this.querySelector('template[data-tts-ss-empty]');
      if (!this.input || !this.panel) return;

      this.cache = new Map();
      this.timer = null;
      this.controller = null;

      this.panel.id = this.panel.id || `tts-ss-panel-${Math.random().toString(36).slice(2, 8)}`;
      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('aria-controls', this.panel.id);
      this.input.setAttribute('aria-expanded', 'false');

      this.onInput = this.onInput.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onDocPointer = this.onDocPointer.bind(this);
      this.onClick = this.onClick.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.onFocus);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('click', this.onClick);
      document.addEventListener('pointerdown', this.onDocPointer);
    }

    disconnectedCallback() {
      if (!this.input) return;
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('focus', this.onFocus);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('pointerdown', this.onDocPointer);
    }

    get query() {
      return this.input.value.trim();
    }

    onInput() {
      window.clearTimeout(this.timer);
      if (this.query === '') {
        if (this.controller) this.controller.abort();
        this.showEmptyState();
        return;
      }
      this.timer = window.setTimeout(() => this.search(this.query), DEBOUNCE_MS);
    }

    onFocus() {
      if (this.query === '') {
        this.showEmptyState();
      } else if (this.panel.innerHTML.trim() !== '') {
        this.open();
      } else {
        this.search(this.query);
      }
    }

    onClick(event) {
      if (event.target.closest('[data-tts-ss-clear-recent]')) {
        event.preventDefault();
        recent.clear();
        this.showEmptyState();
        this.input.focus();
        return;
      }
      // Clicking a product/page/category result still counts as a search.
      if (event.target.closest('[data-tts-ss-option]') && this.query !== '') {
        recent.add(this.query);
      }
    }

    showEmptyState() {
      const extra = this.emptyTemplate ? this.emptyTemplate.innerHTML.trim() : '';
      const html = recent.html() + extra;
      if (html) {
        this.panel.innerHTML = html;
        // Recent searches on their own only need a small panel.
        this.open(extra === '' ? 420 : 880);
      } else {
        this.close();
        this.panel.innerHTML = '';
      }
    }

    async search(query) {
      if (this.cache.has(query)) {
        this.render(this.cache.get(query), query);
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      // limit 10 so up to 6 products remain after hidden ones are skipped
      const url = `${root()}search/suggest?q=${encodeURIComponent(query)}&section_id=${SECTION_ID}` +
        '&resources[type]=query,product,collection,page,article' +
        '&resources[limit]=10&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last';

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) return;
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('.shopify-section') || doc.body;
        const html = section.innerHTML;
        this.cache.set(query, html);
        this.render(html, query);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('TTS instant search failed:', error);
      }
    }

    render(html, query) {
      // Ignore stale responses the user has typed past.
      if (query !== this.query) return;
      this.panel.innerHTML = html;
      this.open();

      if (this.status) {
        const results = this.panel.querySelector('[data-tts-ss-count]');
        const count = results ? Number(results.getAttribute('data-tts-ss-count')) : 0;
        this.status.textContent = count > 0
          ? `${count} product${count === 1 ? '' : 's'} found. Use the down arrow to browse results.`
          : `No products found for ${query}.`;
      }
    }

    open(maxWidth = 880) {
      // The panel is right-aligned to the search box; keep its left edge on screen.
      const room = this.getBoundingClientRect().right - 16;
      this.panel.style.width = `${Math.max(280, Math.min(maxWidth, room))}px`;
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
    }

    options() {
      return Array.from(this.panel.querySelectorAll('[data-tts-ss-option]'));
    }

    onKeydown(event) {
      if (event.key === 'Enter' && event.target === this.input) {
        recent.add(this.query);
      }
      if (event.key === 'Escape') {
        if (!this.panel.hidden) {
          event.preventDefault();
          this.close();
          this.input.focus();
        }
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const options = this.options();
      if (this.panel.hidden || options.length === 0) return;
      event.preventDefault();

      const index = options.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        options[Math.min(index + 1, options.length - 1)].focus();
      } else if (index <= 0) {
        this.input.focus();
      } else {
        options[index - 1].focus();
      }
    }

    onFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      // Let clicks inside the panel land before closing.
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 150);
    }

    onDocPointer(event) {
      if (!this.contains(event.target)) this.close();
    }
  }

  customElements.define('tts-instant-search', TtsInstantSearch);

  // ---- Phone/tablet search dropdown (theme's <predictive-search>) ----
  // Adds "Recent searches" under its search bar while the box is empty.
  const enhanceDropdown = () => {
    const search = document.querySelector('search-dropdown predictive-search');
    if (!search || search.querySelector('[data-tts-ss-recent-mobile]')) return;
    const input = search.querySelector('[data-predictive-search-input]');
    const bar = search.querySelector('.predictive-search__search-bar');
    if (!input || !bar) return;

    const box = document.createElement('div');
    box.setAttribute('data-tts-ss-recent-mobile', '');
    box.hidden = true;
    bar.insertAdjacentElement('afterend', box);

    const update = () => {
      const html = input.value.trim() === '' ? recent.html() : '';
      box.innerHTML = html;
      box.hidden = html === '';
    };

    input.addEventListener('input', update);
    input.addEventListener('focus', update);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') recent.add(input.value);
    });
    box.addEventListener('click', (event) => {
      if (event.target.closest('[data-tts-ss-clear-recent]')) {
        event.preventDefault();
        recent.clear();
        update();
        input.focus();
      }
    });
    search.addEventListener('click', (event) => {
      if (event.target.closest('[data-predictive-search-results] [data-tts-ss-option]') && input.value.trim() !== '') {
        recent.add(input.value);
      }
    });
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceDropdown);
  } else {
    enhanceDropdown();
  }
})();
